/**
 * Alpine.js Editor Base Utilities
 * Common functionality for all profile inline editors
 */

import { initializeJwtToken, clearJwtToken } from './jwtTokenManager.js';

export const editorBase = {
    // Request queue to prevent race conditions
    _requestQueue: new Map(),
    
    /**
     * Common error handler for all editors
     * Extracts meaningful error messages from API responses
     */
    async handleApiError(response, fieldName) {
        let errorMessage = 'Failed to save changes';
        
        try {
            const data = await response.json();
            
            if (data.error) {
                // Priority: field-specific error > general error message > error code
                if (data.error.errors && data.error.errors[fieldName]) {
                    errorMessage = data.error.errors[fieldName].message || 
                                  data.error.errors[fieldName].code;
                } else {
                    errorMessage = data.error.message || data.error.code || errorMessage;
                }
            }
        } catch (e) {
            // Response might not be JSON
            if (response.statusText) {
                errorMessage = `Error ${response.status}: ${response.statusText}`;
            }
        }
        
        return errorMessage;
    },
    
    /**
     * Get authentication token from localStorage
     */
    getAuthToken() {
        try {
            const reduxState = localStorage.getItem('redux-state');
            if (reduxState) {
                const state = JSON.parse(reduxState);
                return state?.auth?.token;
            }
        } catch (e) {
            console.warn('Failed to parse auth token from localStorage:', e.message);
        }
        return null;
    },

    /**
     * Get CSRF token from meta tag
     */
    getCsrfToken() {
        const metaTag = document.querySelector('meta[name="csrf-token"]') || 
                       document.querySelector('meta[name="csrf_token"]');
        return metaTag ? metaTag.getAttribute('content') : null;
    },

    /**
     * Common API request wrapper for saving fields
     * Includes request queuing to prevent race conditions
     * Automatically refreshes expired tokens and retries
     */
    async saveField(endpoint, fieldName, value, extraFields = {}) {
        const requestKey = `${endpoint}:${fieldName}`;

        // If there's already a pending request for this endpoint/field, wait for it
        if (this._requestQueue.has(requestKey)) {
            try {
                await this._requestQueue.get(requestKey);
            } catch (e) {
                // Previous request failed, log but continue with new one
                console.warn(`Previous request for ${requestKey} failed:`, e.message);
            }
        }

        // Create new request promise with token refresh retry
        const requestPromise = this._performSaveWithRetry(endpoint, fieldName, value, extraFields);
        this._requestQueue.set(requestKey, requestPromise);

        try {
            const result = await requestPromise;
            this._requestQueue.delete(requestKey);
            return result;
        } catch (error) {
            this._requestQueue.delete(requestKey);
            throw error;
        }
    },

    /**
     * Perform save request with automatic token refresh on auth errors
     */
    async _performSaveWithRetry(endpoint, fieldName, value, extraFields = {}, isRetry = false) {
        try {
            return await this._performSaveRequest(endpoint, fieldName, value, extraFields);
        } catch (error) {
            // If this is an auth/token error and not already a retry, refresh token and retry once
            if (!isRetry && this._isTokenError(error)) {
                clearJwtToken();
                const tokenData = await initializeJwtToken();
                if (tokenData?.auth?.token) {
                    return await this._performSaveWithRetry(endpoint, fieldName, value, extraFields, true);
                }
            }
            throw error;
        }
    },

    /**
     * Check if an error indicates a token/auth problem
     * @param {Error} error - Error object with optional statusCode property
     */
    _isTokenError(error) {
        // Check HTTP status code first (most reliable)
        if (error.statusCode === 401 || error.statusCode === 403) {
            return true;
        }

        // Fall back to specific message patterns for non-HTTP errors
        const message = (error.message || '').toLowerCase();
        return message.includes('token is missing') ||
               message.includes('token expired') ||
               message.includes('invalid token');
    },
    
    /**
     * Perform the actual save request
     */
    async _performSaveRequest(endpoint, fieldName, value, extraFields = {}) {
        // Get tokens
        const authToken = this.getAuthToken();
        const csrfToken = this.getCsrfToken();
        
        if (!authToken) {
            throw new Error('Login token is missing or invalid');
        }

        const headers = {
            'X-Requested-With': 'XMLHttpRequest',
            'X-AUTH-TOKEN': authToken
            // Note: Don't set Content-Type for FormData, browser sets it automatically with boundary
        };

        // Add CSRF token if available
        if (csrfToken) {
            headers['X-CSRF-TOKEN'] = csrfToken;
        }

        // Build request body as FormData (API expects form data, not JSON)
        const formData = new FormData();
        formData.append(fieldName, value);
        Object.entries(extraFields).forEach(([name, extraValue]) => {
            if (typeof extraValue === 'string') {
                formData.append(name, extraValue);
            }
        });
        
        const response = await fetch(endpoint, {
            method: 'PUT',
            headers: headers,
            credentials: 'include',
            body: formData,
            signal: AbortSignal.timeout(10000) // 10 second timeout
        });
        
        if (!response.ok) {
            const errorMessage = await this.handleApiError(response, fieldName);
            const error = new Error(errorMessage);
            error.statusCode = response.status;
            throw error;
        }
        
        return await response.json();
    },

    /**
     * Show success feedback with animation
     */
    showSuccess(element, message = 'Saved') {
        // Create or update success element
        let successEl = element.querySelector('.inline-edit-success');
        if (!successEl) {
            successEl = document.createElement('div');
            successEl.className = 'inline-edit-success';
            element.appendChild(successEl);
        }
        
        successEl.textContent = message;
        successEl.classList.add('show');
        
        setTimeout(() => {
            successEl.classList.remove('show');
        }, 800);
    },

    /**
     * Escape HTML entities to prevent XSS
     */
    escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    },

    /**
     * Decode HTML entities (reverse of escapeHtml)
     * Used to convert HTML-encoded data attributes back to plain text
     * Safe implementation that doesn't execute scripts
     */
    decodeHtml(text) {
        // Use DOMParser for safe HTML entity decoding without script execution
        const parser = new DOMParser();
        const doc = parser.parseFromString(`<!DOCTYPE html><body>${text}`, 'text/html');
        return doc.body.textContent || '';
    },

    /**
     * Decode HTML entities with multiple passes to catch nested encoding
     */
    decodeHtmlMultiPass(text) {
        let decoded = text;
        for (let i = 0; i < 3; i++) {
            const next = this.decodeHtml(decoded);
            if (next === decoded) break;
            decoded = next;
        }
        return decoded;
    },

    /**
     * Escape for use in HTML attributes (includes quotes)
     */
    escapeAttr(text) {
        return text
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    },

    /**
     * Check if a URL is safe (no javascript:, vbscript:, data: protocols)
     * Note: Server-side only blocks javascript/vbscript. We also block data:
     * URLs client-side as an additional security measure against XSS payloads.
     */
    isSafeUrl(url) {
        if (!url) return false;
        const decoded = this.decodeHtmlMultiPass(url);
        // Remove whitespace and check for dangerous protocols
        const normalized = decoded.replace(/\s+/g, '').toLowerCase();
        if (/^(?:javascript|vbscript|data):/i.test(normalized)) {
            return false;
        }
        return true;
    },

    /**
     * Sanitize URL for use in href/src attributes
     * Returns safe URL or empty string if dangerous
     * Note: Assumes input is already HTML-escaped by formatWithBBCode
     */
    sanitizeUrl(url) {
        if (!this.isSafeUrl(url)) {
            return '';
        }
        return url;
    },

    /**
     * Check if URL is internal (interpals.net)
     * Expects already-decoded URL (no HTML entities)
     */
    isInternalUrl(decodedUrl) {
        return /^(https?:\/\/)?(www\.)?interpals\.net(\/|$)/i.test(decodedUrl) ||
               !/^https?:\/\//i.test(decodedUrl); // Relative URLs are internal
    },

    /**
     * Get URL for href attribute, routing external URLs through redirect
     * Matches server-side IPBBCodeParser behavior
     */
    getHrefUrl(url) {
        const decoded = this.decodeHtmlMultiPass(url);
        if (this.isInternalUrl(decoded)) {
            // Internal URL - escape for attribute safety
            return this.escapeAttr(decoded);
        }
        // External URLs go through redirect for tracking/safety
        return '/app/redir?href=' + encodeURIComponent(decoded);
    },

    /**
     * Format text with BBCode support (for text fields)
     * Matches server-side Quicky_BBcode parser tags
     */
    formatWithBBCode(text) {
        if (!text) return '';

        // First escape HTML to prevent XSS
        let safe = this.escapeHtml(text);

        // Apply BBCode transformations (order matters for nested tags)
        return safe
            // Basic formatting
            .replace(/\[b\](.*?)\[\/b\]/gi, '<strong>$1</strong>')
            .replace(/\[i\](.*?)\[\/i\]/gi, '<em>$1</em>')
            .replace(/\[u\](.*?)\[\/u\]/gi, '<u>$1</u>')
            .replace(/\[s\](.*?)\[\/s\]/gi, '<s>$1</s>')
            // Size
            .replace(/\[size=(\d+)\](.*?)\[\/size\]/gi, '<span style="font-size:$1pt">$2</span>')
            // Color
            .replace(/\[color=([#\w]+)\](.*?)\[\/color\]/gi, (match, color, content) => {
                const safeColor = color.replace(/[^#\w]/g, '');
                return `<span style="color:${safeColor}">${content}</span>`;
            })
            // URLs - sanitize to prevent javascript: XSS, route external through /app/redir
            .replace(/\[url=([^\]]+)\](.*?)\[\/url\]/gi, (match, url, linkText) => {
                const safeUrl = this.sanitizeUrl(url);
                if (!safeUrl) return linkText; // Strip dangerous URL, keep text
                const hrefUrl = this.getHrefUrl(safeUrl);
                return `<a href="${hrefUrl}" target="_blank" rel="noopener noreferrer">${linkText}</a>`;
            })
            .replace(/\[url\](.*?)\[\/url\]/gi, (match, url) => {
                const safeUrl = this.sanitizeUrl(url);
                if (!safeUrl) return url; // Show as plain text (already escaped)
                const hrefUrl = this.getHrefUrl(safeUrl);
                return `<a href="${hrefUrl}" target="_blank" rel="noopener noreferrer">${safeUrl}</a>`;
            })
            // Images - sanitize URL and proxy through image service like server-side
            .replace(/\[img\](.*?)\[\/img\]/gi, (match, imgUrl) => {
                if (!this.isSafeUrl(imgUrl)) return ''; // Strip dangerous URLs entirely
                // Decode HTML entities before URI encoding (imgUrl has &amp; etc. from escapeHtml)
                const decodedUrl = this.decodeHtmlMultiPass(imgUrl);
                const encodedUrl = encodeURIComponent(decodedUrl);
                return `<img src="/app/img?url=${encodedUrl}" style="max-width:100%;" loading="lazy" />`;
            })
            // YouTube - safe pattern: alphanumeric, hyphen, underscore only
            .replace(/\[youtube\]([a-zA-Z0-9_-]+)\[\/youtube\]/gi, (match, videoId) => {
                return `<iframe width="420" height="315" src="https://www.youtube.com/embed/${videoId}" frameborder="0" allowfullscreen loading="lazy"></iframe>`;
            })
            // Quote with author
            .replace(/\[quote=([^\]]+)\](.*?)\[\/quote\]/gis, (match, author, content) => {
                const safeAuthor = this.escapeHtml(author);
                return `<blockquote class="border-l-4 border-gray-300 pl-4 my-2 text-gray-600"><em>Original by: ${safeAuthor}</em><br>${content}</blockquote>`;
            })
            // Quote without author
            .replace(/\[quote\](.*?)\[\/quote\]/gis, '<blockquote class="border-l-4 border-gray-300 pl-4 my-2 text-gray-600">$1</blockquote>')
            // Lists
            .replace(/\[list\](.*?)\[\/list\]/gis, (match, content) => {
                const items = content.split(/\[\*\]/).filter(item => item.trim());
                const listItems = items.map(item => `<li>${item.trim()}</li>`).join('');
                return `<ul class="list-disc pl-6 my-2">${listItems}</ul>`;
            })
            // Alignment
            .replace(/\[align=(left|right|center)\](.*?)\[\/align\]/gis, '<span style="display:block;text-align:$1">$2</span>')
            // Autourl: convert bare URLs to links (skip URLs already inside <a> tags from [url] BBCode)
            .replace(/((?:<a\s[^>]*>[\s\S]*?<\/a>)|(?:https?:\/\/[^\s<>\[\]"']+))/gi, (match) => {
                if (match.startsWith('<a ')) return match;
                const safeUrl = this.sanitizeUrl(match);
                if (!safeUrl) return match;
                const hrefUrl = this.getHrefUrl(safeUrl);
                return `<a href="${hrefUrl}" target="_blank" rel="noopener noreferrer">${match}</a>`;
            })
            // Newlines to breaks (last)
            .replace(/\n/g, '<br>');
    },

    /**
     * Get the appropriate API endpoint for a field
     */
    getEndpointForField(fieldName) {
        const endpoints = {
            // Status field
            status: '/v1/user-status/self',
            
            // User account fields
            name: '/v1/account/self',
            occupation: '/v1/account/self',
            sex: '/v1/account/self',
            
            // Profile/about fields
            description: '/v1/user-about/self',
            requests: '/v1/user-about/self',
            hobbies: '/v1/user-about/self',
            music: '/v1/user-about/self',
            movies: '/v1/user-about/self',
            tv: '/v1/user-about/self',
            books: '/v1/user-about/self',
            quotes: '/v1/user-about/self',
            langex_desc: '/v1/user-about/self',
            education_level: '/v1/user-about/self',
            education_desc: '/v1/user-about/self',
            relationship: '/v1/user-about/self',
            website: '/v1/user-about/self',
            lfor: '/v1/user-about/self'
        };
        
        return endpoints[fieldName] || '/v1/user-about/self';
    },

    /**
     * Validate a field value against the limits the editor is already advertising.
     *
     * The caller passes the limits it rendered rather than this function looking
     * them up, so the number in the "n/max characters" counter and the number the
     * save is checked against are the same value by construction. A lookup table
     * here would be a second copy, free to drift from the one the template
     * rendered.
     *
     * @param {string} fieldName
     * @param {string} value
     * @param {{minLength?: number, maxLength?: number}} limits As advertised by the caller.
     */
    validateField(fieldName, value, limits = {}) {
        const { minLength, maxLength } = limits;

        const trimmedLength = value.trim().length;
        if (minLength && trimmedLength > 0 && trimmedLength < minLength) {
            return {
                valid: false,
                error: window.I18n?.has('validation.min.string')
                    ? window.t('validation.min.string', { min: minLength })
                    : `Must be at least ${minLength} characters`
            };
        }

        if (maxLength && value.length > maxLength) {
            return {
                valid: false,
                error: window.I18n?.has('validation.max.string')
                    ? window.t('validation.max.string', { max: maxLength })
                    : `Must be fewer than ${maxLength} characters`
            };
        }

        // Website URL validation
        if (fieldName === 'website' && value) {
            const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
            if (!urlPattern.test(value)) {
                return {
                    valid: false,
                    error: 'Please enter a valid URL'
                };
            }
        }
        
        return { valid: true };
    },

    /**
     * Auto-resize textarea based on content
     */
    autoResizeTextarea(textarea) {
        if (!textarea) return;
        
        // Reset height to auto to get the correct scrollHeight
        textarea.style.height = 'auto';
        
        // Set height to scrollHeight with a minimum
        const minHeight = 100;
        const newHeight = Math.max(textarea.scrollHeight, minHeight);
        textarea.style.height = `${newHeight}px`;
    },

    /**
     * Common initialization for editor components
     */
    initEditor(component) {
        // Set up cleanup for timeouts and event listeners
        component._timeoutIds = new Set();
        component._eventListeners = [];
        
        // Helper to add event listener with cleanup tracking
        component._addEventListener = (target, type, listener, options) => {
            target.addEventListener(type, listener, options);
            component._eventListeners.push({ target, type, listener, options });
        };
        
        // Cleanup function - only if $cleanup is available (Alpine 3.x)
        if (typeof component.$cleanup === 'function') {
            component.$cleanup(() => {
                // Clear timeouts
                component._timeoutIds.forEach(id => clearTimeout(id));
                component._timeoutIds.clear();
                
                // Remove event listeners
                component._eventListeners.forEach(({ target, type, listener, options }) => {
                    target.removeEventListener(type, listener, options);
                });
                component._eventListeners = [];
            });
        }
    },

    /**
     * Add timeout with cleanup tracking
     */
    setTimeout(component, callback, delay) {
        const id = setTimeout(() => {
            component._timeoutIds.delete(id);
            callback();
        }, delay);

        component._timeoutIds.add(id);
        return id;
    }
};

// Expose getAuthToken globally for legacy code
window.getAuthToken = () => editorBase.getAuthToken();
